import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import api from '../services/api';
import { isMessageId } from '../services/learnerMessages';
import { MESSAGING_REFRESH_EVENT } from './useMessagingWakeups';

export interface Notification {
  id: string;
  kind: string;
  payload: Record<string, unknown>;
  deliveredChannels: Record<string, boolean>;
  readAt: string | null;
  createdAt: string;
}

type Feed = { owner?: string; notifications: Notification[]; unreadCount: number; loading: boolean; error: string | null };
const emptyFeed = (owner?: string): Feed => ({ owner, notifications: [], unreadCount: 0, loading: false, error: null });

function parseFeed(value: unknown): Pick<Feed, 'notifications' | 'unreadCount'> {
  const feed = value as { notifications?: unknown; unread_count?: unknown } | null;
  if (!feed || !Array.isArray(feed.notifications) || !Number.isSafeInteger(feed.unread_count) || Number(feed.unread_count) < 0) throw new Error('Invalid notification feed');
  const notifications = feed.notifications.map((value) => {
    const row = value as Notification;
    if (!row || !isMessageId(row.id) || typeof row.kind !== 'string' || typeof row.createdAt !== 'string' ||
        !Number.isFinite(Date.parse(row.createdAt)) || !(row.readAt === null || typeof row.readAt === 'string') ||
        !row.payload || typeof row.payload !== 'object' || Array.isArray(row.payload)) throw new Error('Invalid notification');
    return { ...row, deliveredChannels: row.deliveredChannels ?? {} };
  });
  return { notifications, unreadCount: Number(feed.unread_count) };
}

/** Per-subject feed. Realtime only invalidates; authenticated HTTP owns content. */
export function useNotifications() {
  const auth = useAuth();
  const subject = auth.isAuthenticated ? auth.user?.profile.sub : undefined;
  const token = auth.isAuthenticated ? auth.user?.access_token : undefined;
  const identity = useRef({ subject, token });
  if (identity.current.subject !== subject || identity.current.token !== token) identity.current = { subject, token };
  const mounted = useRef(true);
  const request = useRef(0);
  const [feed, setFeed] = useState<Feed>(() => emptyFeed(subject));
  const visible = feed.owner === subject ? feed : emptyFeed(subject);

  const load = useCallback(async () => {
    const scope = identity.current;
    if (!scope.subject || !scope.token || scope.subject !== subject) return;
    const ordinal = ++request.current;
    const current = () => mounted.current && identity.current === scope && request.current === ordinal;
    setFeed((prev) => ({ ...(prev.owner === subject ? prev : emptyFeed(subject)), loading: true }));
    try {
      const response = await api.get('/notifications', { params: { limit: 25 }, headers: { Authorization: `Bearer ${scope.token}` } });
      const parsed = parseFeed(response.data);
      if (current()) setFeed({ owner: subject, ...parsed, loading: false, error: null });
    } catch {
      if (current()) setFeed((prev) => ({ ...prev, loading: false, error: 'Notifications could not be refreshed. Try again.' }));
    }
  }, [subject]);

  useEffect(() => {
    mounted.current = true;
    if (!subject || !token) setFeed(emptyFeed(subject));
    else void load();
    const refresh = () => { if (document.visibilityState !== 'hidden') void load(); };
    const timer = subject && token ? setInterval(refresh, 30_000) : undefined;
    window.addEventListener(MESSAGING_REFRESH_EVENT, refresh);
    return () => {
      mounted.current = false;
      request.current += 1;
      if (timer) clearInterval(timer);
      window.removeEventListener(MESSAGING_REFRESH_EVENT, refresh);
    };
  }, [subject, token, load]);

  const markRead = useCallback(async (id: string) => {
    const scope = identity.current;
    if (!scope.subject || !scope.token || !isMessageId(id) || feed.owner !== scope.subject ||
        !feed.notifications.some((notification) => notification.id === id && !notification.readAt)) return;
    try {
      await api.patch(`/notifications/${id}/read`, {}, { headers: { Authorization: `Bearer ${scope.token}` } });
      if (mounted.current && identity.current === scope) await load();
    } catch {
      if (mounted.current && identity.current === scope) setFeed((prev) => ({ ...prev, error: 'Read status could not be confirmed. Try again.' }));
    }
  }, [feed.owner, feed.notifications, load]);

  const markAllRead = useCallback(async () => {
    const scope = identity.current;
    if (!scope.subject || !scope.token || feed.owner !== scope.subject || !feed.unreadCount) return;
    try {
      await api.post('/notifications/mark-all-read', {}, { headers: { Authorization: `Bearer ${scope.token}` } });
      if (mounted.current && identity.current === scope) await load();
    } catch {
      if (mounted.current && identity.current === scope) setFeed((prev) => ({ ...prev, error: 'Read status could not be confirmed. Try again.' }));
    }
  }, [feed.owner, feed.unreadCount, load]);

  return { ...visible, reload: load, markRead, markAllRead };
}
